import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Building2,
  ArrowLeft,
  FileText
} from 'lucide-react';

const DPA_TITLE = 'Data Processing Agreement — MyPropOps';
const DPA_DESCRIPTION =
  'MyPropOps Data Processing Agreement describing how personal data is processed on behalf of customer organizations, sub-processors, data subject rights, and security measures.';

const DPA = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = DPA_TITLE;

    let metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription ? metaDescription.getAttribute('content') : null;
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', DPA_DESCRIPTION);

    return () => {
      document.title = previousTitle;
      if (previousDescription !== null && metaDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">MyPropOps</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button>Start Free</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-24 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/security" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Security
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Data Processing Agreement</h1>
            </div>
          </div>

          <p className="text-muted-foreground mb-2">
            This Data Processing Agreement ("DPA") describes how MyPropOps processes personal data on behalf of customer organizations (Controllers) using the MyPropOps platform. By using MyPropOps, Controllers authorize MyPropOps to process personal data of their tenants, contractors, and team members in accordance with this agreement and applicable data protection laws including the General Data Protection Regulation (GDPR) and the California Consumer Privacy Act (CCPA / CPRA).
          </p>

          <p className="text-sm text-muted-foreground">Last updated: May 8, 2026.</p>
        </div>
      </section>

      {/* DPA Content */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8">

          {/* 1. Roles */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">1. Roles</h2>

              <h3 className="text-lg font-medium mt-4 mb-2">1.1 Controller and Processor</h3>
              <p className="text-muted-foreground">
                The customer organization using MyPropOps is the Controller of personal data submitted to the platform. MyPropOps acts as the Processor, processing personal data only on documented instructions from the Controller, including the act of using the platform's features for their intended purposes.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">1.2 Data Subjects</h3>
              <p className="text-muted-foreground">
                Data Subjects under this DPA include tenants, prospective tenants, contractors, household members, and team members whose personal data is processed within the platform.
              </p>
            </CardContent>
          </Card>

          {/* 2. Categories of Personal Data Processed */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">2. Categories of Personal Data Processed</h2>
              <p className="text-muted-foreground mb-4">MyPropOps processes the following categories of personal data:</p>
              <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-6">
                <li>Identity data: names, email addresses, phone numbers</li>
                <li>Tenant profile data: addresses, household composition, housing program participation, voucher numbers, income information</li>
                <li>Property data: lease terms, occupancy records, payment histories</li>
                <li>Inspection data: photos, findings, compliance status</li>
                <li>Maintenance data: requests, work orders, resolution histories</li>
                <li>Document data: uploaded leases, identification documents, housing program documentation, contractor invoices</li>
                <li>Communication data: messages between Controllers, tenants, and contractors within the platform</li>
                <li>Authentication data: passwords (stored hashed), 2FA tokens</li>
                <li>Usage data: timestamps, IP addresses, audit logs</li>
              </ul>
            </CardContent>
          </Card>

          {/* 3. Sub-Processors */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">3. Sub-Processors</h2>
              <p className="text-muted-foreground mb-4">
                MyPropOps engages the following sub-processors to deliver the platform. By using MyPropOps, the Controller consents to the use of these sub-processors. A current list is maintained at <Link to="/legal/subprocessors" className="text-primary hover:underline">/legal/subprocessors</Link>.
              </p>
              <p className="text-muted-foreground">
                MyPropOps will provide notice of any new sub-processor before authorizing them to process personal data, allowing Controllers a reasonable opportunity to object on legitimate grounds.
              </p>
            </CardContent>
          </Card>

          {/* 4. Data Subject Rights */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">4. Data Subject Rights</h2>

              <h3 className="text-lg font-medium mt-4 mb-2">4.1 Access, Portability, Rectification, and Deletion</h3>
              <p className="text-muted-foreground">
                Data Subjects may exercise their rights under applicable data protection law by contacting MyPropOps at <a href="mailto:privacy@mypropops.com" className="text-primary hover:underline">privacy@mypropops.com</a>. MyPropOps will respond to verified requests within thirty (30) days.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">4.2 Tenant Deletion Requests</h3>
              <p className="text-muted-foreground">
                When a tenant requests deletion of their personal data, MyPropOps will notify the Controller (the property manager or landlord). The Controller has fourteen (14) days to invoke a legitimate-interest exception, including ongoing lease obligations, regulatory record-keeping requirements under landlord-tenant law, housing program audit trails, tax record-keeping, or any retention obligation imposed by applicable law. If the Controller does not invoke an exception within fourteen days, MyPropOps will proceed with deletion of personal data that is not itself subject to a separate legal retention obligation.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">4.3 Self-Service Tools (Roadmap)</h3>
              <p className="text-muted-foreground">
                MyPropOps is developing in-platform self-service tools for data export and deletion, expected to be available in 2026. Until those tools are available, Data Subject requests are handled manually by the MyPropOps privacy team.
              </p>
            </CardContent>
          </Card>

          {/* 5. Security Measures */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">5. Security Measures</h2>
              <p className="text-muted-foreground mb-4">
                MyPropOps implements the following technical and organizational measures to protect personal data:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-6">
                <li>Encryption at rest using AES-256</li>
                <li>Encryption in transit using TLS 1.3</li>
                <li>Role-based access controls with organization-level isolation</li>
                <li>Two-factor authentication available on Pro and Enterprise tiers</li>
                <li>Audit logging of every action affecting customer data</li>
                <li>Password storage using industry-standard hashing (bcrypt)</li>
                <li>Regular security review of platform code and infrastructure</li>
              </ul>
            </CardContent>
          </Card>

          {/* 6. Data Retention and Deletion */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">6. Data Retention and Deletion</h2>

              <h3 className="text-lg font-medium mt-4 mb-2">6.1 During the Subscription</h3>
              <p className="text-muted-foreground">
                MyPropOps retains personal data for the duration of the Controller's active subscription, plus any retention periods required by applicable law or invoked by the Controller for legitimate business purposes (including landlord-tenant recordkeeping, housing program audit trails, and tax recordkeeping).
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">6.2 Upon Termination</h3>
              <p className="text-muted-foreground">
                When a Controller cancels their MyPropOps account, the Controller's data is retained in a soft-deleted state for ninety (90) days, during which the Controller may reactivate the account or request a final data export. After ninety days, MyPropOps will permanently delete the Controller's data from primary systems. Backup copies are retained according to MyPropOps's backup retention schedule (currently thirty days) and are then permanently destroyed.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">6.3 Tenant Deletion Following the Procedure in Section 4.2</h3>
              <p className="text-muted-foreground">
                Personal data deleted under Section 4.2 is removed from primary systems within thirty (30) days of the deletion being authorized. Backup copies are removed within MyPropOps's standard backup rotation.
              </p>
            </CardContent>
          </Card>

          {/* 7. Breach Notification */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">7. Breach Notification</h2>
              <p className="text-muted-foreground">
                In the event of a personal data breach affecting customer data, MyPropOps will notify affected Controllers without undue delay and in any event within seventy-two (72) hours of becoming aware of the breach. Notifications will include the nature of the breach, the categories and approximate number of Data Subjects affected, the likely consequences, and the measures MyPropOps has taken or proposes to take.
              </p>
            </CardContent>
          </Card>

          {/* 8. International Data Transfers */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">8. International Data Transfers</h2>
              <p className="text-muted-foreground">
                MyPropOps stores customer data on infrastructure located in the United States. Where personal data is transferred from the European Economic Area, the United Kingdom, or other jurisdictions with cross-border transfer requirements, the transfer is governed by Standard Contractual Clauses or other lawful transfer mechanisms incorporated into MyPropOps's sub-processor agreements.
              </p>
            </CardContent>
          </Card>

          {/* 9. Audit Cooperation */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">9. Audit Cooperation</h2>
              <p className="text-muted-foreground">
                MyPropOps will cooperate with reasonable Controller audit requests on the platform's processing activities. Controllers may request information about MyPropOps's data processing practices by contacting <a href="mailto:privacy@mypropops.com" className="text-primary hover:underline">privacy@mypropops.com</a>. Audit requests will be scheduled to minimize disruption to MyPropOps's services and will be subject to confidentiality obligations.
              </p>
            </CardContent>
          </Card>

          {/* 10. Liability and Indemnification */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">10. Liability and Indemnification</h2>
              <p className="text-muted-foreground">
                This DPA is incorporated into and subject to the MyPropOps Terms of Service. The liability and indemnification provisions of the Terms of Service apply to obligations under this DPA.
              </p>
            </CardContent>
          </Card>

          {/* 11. Changes to This DPA */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">11. Changes to This DPA</h2>
              <p className="text-muted-foreground">
                MyPropOps may update this DPA from time to time. Material changes will be communicated to Controllers via email or in-app notification at least thirty (30) days before the changes take effect.
              </p>
            </CardContent>
          </Card>

          {/* 12. Contact */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">12. Contact</h2>
              <p className="text-muted-foreground">
                For questions about this DPA or to exercise rights under applicable data protection law, contact:
                <br />MyPropOps Privacy Team
                <br />Email: <a href="mailto:privacy@mypropops.com" className="text-primary hover:underline">privacy@mypropops.com</a>
              </p>
            </CardContent>
          </Card>

        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-border mt-12">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            <span className="font-semibold">MyPropOps</span>
          </Link>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/legal/subprocessors" className="hover:text-foreground">Sub-Processors</Link>
            <Link to="/security" className="hover:text-foreground">Security</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DPA;
